import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Clock, Users, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import TrustBlock from "@/components/TrustBlock";
import Testimonials from "@/components/Testimonials";
const SERVICES_PREVIEW = [
  { icon: HeartHandshake, title: "Cuidador Domiciliar", desc: "Acompanhamento diário com carinho e segurança no conforto do lar." },
  { icon: Stethoscope, title: "Home Care", desc: "Equipe multidisciplinar para cuidados de saúde em domicílio." },
  { icon: Clock, title: "Acompanhamento Hospitalar", desc: "Presença qualificada 24h durante internações e procedimentos." },
  { icon: Users, title: "Cuidador por Período", desc: "Plantões diurnos, noturnos ou por hora, conforme sua necessidade." },
];

const WHATSAPP_URL = "https://wa.me/5521974615899?text=Olá!%20Gostaria%20de%20solicitar%20uma%20avaliação.";

export default function Home() {
  return (
    <main>
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <img
          src="/hero-care.webp"
          srcSet="/hero-care-640.webp 640w, /hero-care-1024.webp 1024w, /hero-care.webp 1920w"
          sizes="100vw"
          alt="Cuidado humanizado para idosos"
          width={1920}
          height={1080}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 container section-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight text-balance">
              Cuidado que acolhe,
              <br />
              <span className="text-white">amparo que transforma.</span>
            </h1>
            <p className="text-lg text-white/95 mb-8 max-w-lg leading-relaxed">
              Cuidadores de idosos no Rio de Janeiro com atendimento humanizado, transparente e respeitoso. Sua família merece esse cuidado.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold text-base px-8">
                  Solicitar Avaliação Gratuita
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link to="/servicos">
                <Button size="lg" variant="outline" className="border-primary-foreground/70 text-primary-foreground bg-transparent hover:bg-primary-foreground/20 text-base">
                  Conhecer Serviços
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services preview */}
      <section className="section-padding">
        <div className="container">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-4">Nossos Serviços</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto">
            Soluções completas de cuidado domiciliar, adaptadas à necessidade de cada família.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES_PREVIEW.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-card rounded-xl p-6 border border-border hover:shadow-md transition-shadow"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                  <s.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/servicos">
              <Button variant="outline" size="lg">
                Ver todos os serviços <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <TrustBlock />
      <Testimonials />

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
            Pronto para dar o próximo passo?
          </h2>
          <p className="text-primary-foreground/95 mb-8 max-w-lg mx-auto">
            Solicite uma avaliação gratuita e sem compromisso. Vamos entender juntos o que seu familiar precisa.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8">
                Falar pelo WhatsApp
              </Button>
            </a>
            <Link to="/contato">
              <Button size="lg" variant="outline" className="border-primary-foreground/70 text-primary-foreground bg-transparent hover:bg-primary-foreground/20">
                Enviar Mensagem
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
